import { SvgIcon } from '@progress/kendo-react-common';
import { Loader } from '@progress/kendo-react-indicators';
import React from 'react';
import { ChevronDownOutline, ChevronUpOutline } from '../../../../icons/icons';
import { FERegistrationResponseDTO, LocationApiService } from '../../../../services/axios/axios-api-generated';
import { useFocusedWorkspaceSelector } from '../hooks/use-view';

export function FlightRegistration() {
    const [expanded, setExpanded] = React.useState(false);
    const acid = useFocusedWorkspaceSelector((x) => x.flights.selected[0].details.acid);
    const regId = useFocusedWorkspaceSelector((x) => x.flights.selected[0].details.regId);
    const [id, setId] = React.useState<string | null>(null);
    const [registrationData, setRegistrationData] = React.useState<FERegistrationResponseDTO | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [fetchFailed, setFetchFailed] = React.useState(false);

    React.useEffect(() => {
        if (expanded && id && !registrationData) {
            setIsLoading(true);
            setFetchFailed(false);

            LocationApiService.tailRegistration(
                {
                    body: {
                        id: id
                    }
                },
                { withAuthorization: true }
            )
                .then((response) => {
                    setRegistrationData(response);
                    setFetchFailed(!response);
                })
                .catch((error) => {
                    console.error('Failed to fetch registration data:', error);
                    setFetchFailed(true);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [expanded, id]);

    React.useEffect(() => {
        setExpanded(false);
        setRegistrationData(null);
        setFetchFailed(false);

        if (regId && /^N\d{1,5}[A-Z]{0,2}$/.test(regId)) {
            setId(regId);
        } else if (acid && /^(N|LN|TN)\d+$/.test(acid)) {
            setId(acid);
        } else {
            setId(null);
        }
    }, [acid, regId]);

    const placeholderIfEmpty = (value?: string, placeholder = '-'): string => {
        return value && value.trim() ? value : placeholder;
    };

    const formatDate = (date?: string): string => {
        if (!date) {
            return '-';
        }
        const parsed = new Date(date);
        if (isNaN(parsed.getTime())) {
            return '-';
        }
        return parsed.toLocaleDateString();
    };

    const formatSpeed = (speed?: string): string => {
        if (!speed) {
            return '-';
        }
        const parsed = parseFloat(speed);
        if (isNaN(parsed)) {
            return '-';
        }
        const nauticalMiles = parsed * 0.868976242;
        return `${nauticalMiles.toFixed(0)}/H`;
    };

    if (!id) {
        return null;
    }

    return (
        <>
            <div className="d-flex flight-summary-section-header gap-1 border-top" id="flight-details-flight-registration-header">
                <div
                    className="d-flex gap-2 align-items-center ps-2 py-2 flex-grow-1 flex-shrink-0 cursor-pointer"
                    onClick={() => setExpanded(!expanded)}>
                    <div>Registration information</div>
                    <SvgIcon icon={expanded ? ChevronUpOutline : ChevronDownOutline}></SvgIcon>
                </div>
            </div>
            {expanded && (
                <div className="border-top">
                    {isLoading && (
                        <div className="d-flex justify-content-center align-items-center mt-1 py-2">
                            <Loader size="medium" type="infinite-spinner" />
                        </div>
                    )}
                    {!isLoading && fetchFailed && (
                        <div className="px-3 py-2">
                            <span className="secondary-text-color">No registration data available.</span>
                        </div>
                    )}
                    {!isLoading && !fetchFailed && registrationData && (
                        <div
                            className="px-3 py-2"
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                width: '100%',
                                gridColumnGap: '.375rem',
                                gridRowGap: '.25rem'
                            }}>
                            <span className="secondary-text-color">Registration Number</span>
                            <span>{placeholderIfEmpty(registrationData.registrationNumber)}</span>

                            <span className="secondary-text-color">Serial Number</span>
                            <span>{placeholderIfEmpty(registrationData.serialNumber)}</span>

                            <span className="secondary-text-color">Year Manufactured</span>
                            <span>{placeholderIfEmpty(registrationData.yearManufactured)}</span>

                            <span className="secondary-text-color">Registrant Type</span>
                            <span>{placeholderIfEmpty(registrationData.registrantType)}</span>

                            <span className="secondary-text-color">Registrant Name</span>
                            <span>{placeholderIfEmpty(registrationData.registrantName)}</span>

                            <span className="secondary-text-color">Registrant Street 1</span>
                            <span>{placeholderIfEmpty(registrationData.registrantStreet1)}</span>

                            <span className="secondary-text-color">Registrant Street 2</span>
                            <span>{placeholderIfEmpty(registrationData.registrantStreet2)}</span>

                            <span className="secondary-text-color">Registrant City</span>
                            <span>{placeholderIfEmpty(registrationData.registrantCity)}</span>

                            <span className="secondary-text-color">Registrant State</span>
                            <span>{placeholderIfEmpty(registrationData.registrantState)}</span>

                            <span className="secondary-text-color">Registrant Zip Code</span>
                            <span>{placeholderIfEmpty(registrationData.registrantZipCode)}</span>

                            <span className="secondary-text-color">Registrant Region</span>
                            <span>{placeholderIfEmpty(registrationData.registrantRegion)}</span>

                            <span className="secondary-text-color">Registrant County</span>
                            <span>{placeholderIfEmpty(registrationData.registrantCounty)}</span>

                            <span className="secondary-text-color">Registrant Country Code</span>
                            <span>{placeholderIfEmpty(registrationData.registrantCountryCode)}</span>

                            <span className="secondary-text-color">Last Activity Date</span>
                            <span>{formatDate(registrationData.lastActivityDate)}</span>

                            <span className="secondary-text-color">Certificate Issue Date</span>
                            <span>{formatDate(registrationData.cetificateIssueDate)}</span>

                            <span className="secondary-text-color">Airworthiness Classification</span>
                            <span>{placeholderIfEmpty(registrationData.airworthinessClassification)}</span>

                            <span className="secondary-text-color">Approved Operation</span>
                            <span>{placeholderIfEmpty(registrationData.approvedOperation)}</span>

                            <span className="secondary-text-color">Airworthiness Date</span>
                            <span>{formatDate(registrationData.airworthinessDate)}</span>

                            <span className="secondary-text-color">Status</span>
                            <span>{placeholderIfEmpty(registrationData.status)}</span>

                            <span className="secondary-text-color">Mode S Transponder Code</span>
                            <span>{placeholderIfEmpty(registrationData.modeSTransponderCode)}</span>

                            <span className="secondary-text-color">Fractional Ownership</span>
                            <span>{placeholderIfEmpty(registrationData.fractionalOwnership)}</span>

                            <span className="secondary-text-color">Aircraft Manufacturer Name</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftManufacturerName)}</span>

                            <span className="secondary-text-color">Aircraft Manufacturer Code</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftManufacturerCode)}</span>

                            <span className="secondary-text-color">Aircraft Model Name</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftModelName)}</span>

                            <span className="secondary-text-color">Aircraft Model Code</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftModelCode)}</span>

                            <span className="secondary-text-color">Aircraft Series Code</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftSeriesCode)}</span>

                            <span className="secondary-text-color">Aircraft Type</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftType)}</span>

                            <span className="secondary-text-color">Aircraft Category Code</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftCategoryCode)}</span>

                            <span className="secondary-text-color">Builder Certification Code</span>
                            <span>{placeholderIfEmpty(registrationData.builderCertificationcode)}</span>

                            <span className="secondary-text-color">Number Of Engines</span>
                            <span>{placeholderIfEmpty(registrationData.numberOfEngines)}</span>

                            <span className="secondary-text-color">Number Of Seats</span>
                            <span>{placeholderIfEmpty(registrationData.numberOfSeats)}</span>

                            <span className="secondary-text-color">Aircraft Weight</span>
                            <span>{placeholderIfEmpty(registrationData.aircraftWeight)}</span>

                            <span className="secondary-text-color">Aircraft Cruising Speed</span>
                            <span>{formatSpeed(registrationData.aircraftCruisingSpeed)}</span>

                            <span className="secondary-text-color">Engine Manufacturer Name</span>
                            <span>{placeholderIfEmpty(registrationData.engineManufacturerName)}</span>

                            <span className="secondary-text-color">Engine Manufacturer Code</span>
                            <span>{placeholderIfEmpty(registrationData.engineManufacturerCode)}</span>

                            <span className="secondary-text-color">Engine Model Name</span>
                            <span>{placeholderIfEmpty(registrationData.engineModelName)}</span>

                            <span className="secondary-text-color">Engine Model Code</span>
                            <span>{placeholderIfEmpty(registrationData.engineModelCode)}</span>

                            <span className="secondary-text-color">Engine Type</span>
                            <span>{placeholderIfEmpty(registrationData.engineType)}</span>

                            <span className="secondary-text-color">Engine Horsepower</span>
                            <span>{placeholderIfEmpty(registrationData.engineHorsepower)}</span>

                            <span className="secondary-text-color">Pounds of Thrust</span>
                            <span>{placeholderIfEmpty(registrationData.poundsofThrust)}</span>
                        </div>
                    )}
                </div>
            )}
        </>
    );
}
